
import React from 'react';
import { Link } from 'react-router-dom';

interface InterestLabel {
  text: string;
  icon: string;
  cls: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  image: string;
  description: string;
  rating: number;
  reviews: number;
  pricing: string;
  commission: string;
  tags: string[];
  affiliateLink: string;
}

interface DealHeroProps {
  product: Product;
  interestLabel: InterestLabel;
}

/**
 * DealHero component – displays a product deal with image, rating, pricing, tags and CTA.
 * Includes defensive checks and fallback UI to avoid runtime crashes when data is missing.
 */
export default function DealHero({ product, interestLabel }: DealHeroProps) {
  // Basic validation – if required fields are missing, render a fallback UI.
  if (!product) {
    return (
      <section className="pt-28 pb-16 px-6 lg:px-8 text-center text-white">
        <p>Product data is unavailable.</p>
      </section>
    );
  }

  const {
    name = 'Unnamed Product',
    category = '',
    image = '',
    description = '',
    rating = 0,
    reviews = 0,
    pricing = '',
    commission = '',
    tags = [],
    affiliateLink = '#',
  } = product;

  // Ensure rating is within 0‑5 range.
  const safeRating = Math.min(Math.max(rating, 0), 5);
  const fullStars = Math.floor(safeRating);
  const emptyStars = 5 - fullStars;

  // Helper to format numbers safely.
  const formatReviews = (num: number) => {
    try {
      return num.toLocaleString();
    } catch {
      return `${num}`;
    }
  };

  return (
    <section className="pt-28 pb-16 px-6 lg:px-8 relative overflow-hidden">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-neon/5 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-white/40 mb-10">
          <Link to="/" className="hover:text-white transition-colors cursor-pointer">
            Home
          </Link>
          <i className="ri-arrow-right-s-line text-white/20" />
          <Link to="/websites" className="hover:text-white transition-colors cursor-pointer">
            Deals
          </Link>
          <i className="ri-arrow-right-s-line text-white/20" />
          <span className="text-white/60">{name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image */}
          <div className="relative">
            <div className="w-full h-80 lg:h-96 bg-white/[0.03] border border-white/5 rounded-none overflow-hidden">
              {image ? (
                <img
                  src={image}
                  alt={name}
                  title={`${name} affiliate deal`}
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white/30">
                  No image available
                </div>
              )}
            </div>
            {category && (
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm text-white/80 text-xs font-medium rounded-none">
                {category}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-none text-xs font-semibold ${interestLabel.cls}`}
              >
                <i className={`${interestLabel.icon} text-xs`} />
                {interestLabel.text}
              </span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">{name}</h1>

            <p className="text-white/50 text-base leading-relaxed mb-6">{description}</p>

            {/* Rating */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-1">
                {Array.from({ length: fullStars }).map((_, i) => (
                  <i key={`full-${i}`} className="ri-star-fill text-amber-400 text-sm" />
                ))}
                {Array.from({ length: emptyStars }).map((_, i) => (
                  <i key={`empty-${i}`} className="ri-star-line text-white/20 text-sm" />
                ))}
              </div>
              <span className="text-white/70 text-sm font-medium">{safeRating.toFixed(1)}</span>
              <span className="text-white/30 text-sm">({formatReviews(reviews)} reviews)</span>
            </div>

            {/* Pricing */}
            <div className="flex items-baseline gap-3 mb-8">
              <span className="text-2xl font-bold text-neon">{pricing}</span>
              <span className="text-white/30 text-sm">• {commission} commission</span>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-white/5 border border-white/10 text-white/50 text-xs font-medium rounded-none"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="flex items-center gap-4">
              <a
                href={affiliateLink}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-neon text-black text-sm font-bold rounded-none hover:bg-neon/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get This Deal <i className="ri-arrow-right-up-line" />
              </a>
              <Link
                to="/websites"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/5 border border-white/10 text-white/60 text-sm font-medium rounded-none hover:text-white hover:border-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                All Deals
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
